import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { api } from '../core/trpc'
import { getJwt, removeJwt, storeJwt } from '../utils/jwt'

/**
 * The signed-in user, if there is one.
 *
 * A hook over the shared query cache rather than a context provider — the same shape admin uses.
 * The token in storage is only a claim; `user.getMe` is what settles it, which is why `user` can be
 * undefined while a token exists (loading, or a token the API has since rejected).
 */
export const useAuth = () => {
    const jwt = getJwt()
    const queryClient = useQueryClient()
    const utils = api.useUtils()

    /**
     * The server cannot read a localStorage token, so SSR always emits the signed-out markup. If the
     * client rendered the truth on its first pass it would disagree with that and trip hydration, so
     * the answer stays "unknown" until after mount and both passes match.
     */
    const [isHydrated, setIsHydrated] = useState(false)
    useEffect(() => {
        setIsHydrated(true)
    }, [])

    const { data, refetch, isLoading } = api.user.getMe.useQuery(undefined, {
        enabled: !!jwt,
        staleTime: 5 * 60_000,
        // A rejected token is answered by the 401 handler in router.tsx; retrying only delays it.
        retry: false,
    })

    const login = useCallback(
        async (token: string) => {
            storeJwt(token)
            await refetch()
        },
        [refetch],
    )

    const logout = useCallback(() => {
        removeJwt()
        // clear() rather than invalidate(): invalidating refetches every authed query with no
        // token, which trips the 401 handler and redirects a user who only meant to sign out.
        queryClient.clear()
    }, [queryClient])

    return {
        user: data,
        isAuthenticated: !!jwt && !!data,
        isLoading: !!jwt && isLoading,
        /**
         * Whether the answer is still unknown — not hydrated yet, or a token exists and `getMe` has
         * not answered. Distinct from `isAuthenticated === false`, which means *definitely* signed
         * out: treating the two as the same is what made the header show a sign-in button for a
         * second to people who were already signed in.
         */
        isResolving: !isHydrated || (!!jwt && isLoading),
        login,
        logout,
        utils,
    }
}
